import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import React, { useEffect, useMemo, useState } from "react";

import Grid from "@mui/material/Grid";
import LoadingButton from "@mui/lab/LoadingButton";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
} from "@mui/material";

import Title from "../../../common/Title";

import useAlert from "../../../../hooks/useAlert";
import useAxios from "../../../../hooks/useAxios";
import {
  __fetchKosherResellers,
  __updateNumberTmobilej,
} from "../../../../api/sim";
import { __fetchPlans } from "../../../../api/plan";
import InputFieldAutoComplete from "../../../common/InputFieldAutoComplete";

const TmobilejUpdateNumberForm = () => {
  const [rawData, setRawData] = useState("");
  const [linesData, setLinesData] = useState([]);
  const [formData, setFormData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [kosherOptions, setKosherOptions] = useState([]);
  const [selectedKosherReseller, setSelectedKosherReseller] = useState(null);

  const { setAlertMessage } = useAlert();
  const nav = useNavigate();

  const [response, call, loading] = useAxios({ ...__updateNumberTmobilej });
  const [planResponse, planCall, planLoading] = useAxios({ ...__fetchPlans });
  const [kosherResponse, kosherCall, kosherLoading] = useAxios({
    ...__fetchKosherResellers,
  });

  useEffect(() => {
    planCall({ params: { page_size: 100 } });
  }, []);

  useEffect(() => {
    if (planResponse && planResponse.status) {
      setPlans(planResponse.data.data || planResponse.data);
    }
  }, [planResponse]);

  const lineValidationSchema = yup.object({
    msisdn: yup
      .string("msisdn")
      .min(10, "Should be exactly 10 digits long")
      .max(10, "Should be exactly 10 digits long")
      .required(),
  });

  const formik = useFormik({
    initialValues: {
      kosher_api: false,
      kosher_reseller_email: null,
      reset_usage: true,
      renewal_day: "",
    },
    onSubmit: (values) => {
      setFormData(values);
    },
    validateOnChange: true,
  });

  const planHasData = Boolean(
    selectedPlan?.plan &&
      ["notify_value", "throttle_value", "limit_value"].some(
        (k) => Number(selectedPlan.plan[k]) > 0
      )
  );
  const renewalDayNum = Number(formik.values.renewal_day);
  const renewalDayInvalid =
    planHasData &&
    formik.values.renewal_day !== "" &&
    (!Number.isInteger(renewalDayNum) || renewalDayNum < 1 || renewalDayNum > 31);

  const validateFunction = (data) => {
    const errors = {};
    const formattedLines = [];
    if (!data) return errors;

    const msisdns = [];
    const lines = data.split("\n");
    lines.some((line, idx) => {
      const fields = line.split(",");
      if (fields.length < 1) {
        errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
        return true;
      }
      try {
        const obtainedData = { msisdn: fields[0].trim() };
        lineValidationSchema.validateSync(obtainedData);
        if (isNaN(obtainedData.msisdn)) throw new Error("Invalid Number");
        formattedLines.push(obtainedData);
        msisdns.push(obtainedData.msisdn);
        if (msisdns.indexOf(obtainedData.msisdn) !== idx) {
          errors.lines = `Duplicate msisdn value in line ${idx + 1}`;
          return true;
        }
      } catch (err) {
        errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
        return true;
      }
      return false;
    });

    if (!errors.lines) setLinesData(formattedLines);
    return errors;
  };

  const kosherOptionsWithSelected = useMemo(() => {
    if (!selectedKosherReseller) return kosherOptions;
    if (kosherOptions.some((option) => option.id === selectedKosherReseller.id)) {
      return kosherOptions;
    }
    return [selectedKosherReseller, ...kosherOptions];
  }, [kosherOptions, selectedKosherReseller]);

  useEffect(() => {
    if (formData) {
      const requestData = {
        lines: [...linesData],
      };

      if (selectedPlan) {
        requestData.plan_id = selectedPlan.id;
        requestData.plan_name = selectedPlan.name;
        if (planHasData) {
          requestData.reset_usage = formik.values.reset_usage;
          if (formik.values.renewal_day !== "") {
            requestData.renewal_day = Number(formik.values.renewal_day);
          }
        }
      }

      if (formik.values.kosher_api) {
        requestData.kosher_api = true;
        requestData.kosher_reseller_email = formik.values.kosher_reseller_email;
      }

      call({ data: requestData });
    }
  }, [formData]);

  useEffect(() => {
    if (!response) return;
    if (response.status) {
      setAlertMessage({ type: "success", message: "Update Number queued" });
      setLinesData([]);
      nav("/tmobilej/update_number");
    }
    formik.setSubmitting(false);
  }, [response, setAlertMessage, nav, formik]);

  useEffect(() => {
    if (kosherResponse && kosherResponse.status) {
      setKosherOptions(kosherResponse.data.resellers || []);
    }
  }, [kosherResponse]);

  const handleFile = (file) => {
    const reader = new FileReader();
    reader.onload = ({ target }) => {
      setRawData(target.result);
      formik.setErrors({
        ...formik.errors,
        ...validateFunction(target.result),
      });
    };
    reader.readAsText(file);
  };

  const handleFileChange = (e) => {
    if (!e.target.files.length) return;
    const file = e.target.files[0];
    if (!file.name.endsWith(".csv")) {
      formik.setErrors({ ...formik.errors, upload: "Please input a csv file" });
      return;
    }
    handleFile(file);
  };

  if (planLoading) {
    return <Title content="Please wait..." variant="subtitle2" gutterBottom />;
  }

  return (
    <>
      <Box
        sx={{ width: "100%" }}
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Title
          variant="h5"
          content="Update Number"
          sx={{ mb: 3 }}
          gutterBottom
        />

        <Grid
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={12} minWidth={350}>
            <FormControl sx={{ width: "100%" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.kosher_api}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      formik.setFieldValue("kosher_api", checked);
                      if (!checked) {
                        formik.setFieldValue("kosher_reseller_email", null);
                        setSelectedKosherReseller(null);
                        return;
                      }
                      if (!kosherOptions.length) {
                        kosherCall({ params: { limit: 100, offset: 0 } });
                      }
                    }}
                  />
                }
                label="Enable Kosher reseller assign (optional)"
              />
              <FormHelperText>
                If enabled, number assignment to reseller runs after all update
                operations complete.
              </FormHelperText>
            </FormControl>
          </Grid>

          {formik.values.kosher_api ? (
            <Grid item xs={12} minWidth={350}>
              <FormControl sx={{ width: "100%" }}>
                <InputFieldAutoComplete
                  isSelect
                  options={kosherOptionsWithSelected}
                  loading={kosherLoading}
                  getOptionLabel={(option) => {
                    if (!option) return "";
                    const first = option.first_name || "";
                    const last = option.last_name || "";
                    const email = option.email || "";
                    return `${first} ${last}`.trim()
                      ? `${first} ${last}`.trim() + ` (${email})`
                      : email;
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                  }
                  value={selectedKosherReseller}
                  label="Kosher Reseller"
                  onChange={(_, value) => {
                    setSelectedKosherReseller(value || null);
                    formik.setFieldValue(
                      "kosher_reseller_email",
                      value?.email ?? null
                    );
                    formik.setFieldTouched("kosher_reseller_email", true);
                  }}
                  filterOptions={(options) => options}
                  noOptionsText={
                    kosherLoading ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={16} />
                        Loading...
                      </Box>
                    ) : (
                      "No reseller found"
                    )
                  }
                />
                {formik.touched.kosher_reseller_email &&
                !formik.values.kosher_reseller_email ? (
                  <FormHelperText error>
                    Kosher reseller is required when Kosher API is enabled
                  </FormHelperText>
                ) : (
                  ""
                )}
              </FormControl>
            </Grid>
          ) : null}

          <Grid item xs={12} minWidth={350}>
            <FormControl sx={{ width: "100%" }}>
              <InputFieldAutoComplete
                isSelect
                options={[
                  { value: null, label: "None" },
                  ...plans.map((plan) => ({
                    value: plan.id,
                    label: plan.plan_name || plan.name,
                  })),
                ]}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                name="plan"
                label="Plan (optional)"
                id="plan"
                onChange={(e, value) => {
                  if (value?.value) {
                    const plan = plans.find((p) => p.id === value.value);
                    setSelectedPlan({
                      id: value.value,
                      name: value.label,
                      plan,
                    });
                  } else {
                    setSelectedPlan(null);
                  }
                }}
              />
              <FormHelperText>
                Select a plan to apply plan-based settings, or leave as "None"
                to use current SIM info values.
              </FormHelperText>
            </FormControl>
          </Grid>

          {planHasData ? (
            <>
              <Grid item xs={12} minWidth={350}>
                <FormControl sx={{ width: "100%" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formik.values.reset_usage}
                        onChange={(e) =>
                          formik.setFieldValue("reset_usage", e.target.checked)
                        }
                      />
                    }
                    label="Reset data usage"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} minWidth={350}>
                <FormControl sx={{ width: "100%" }} error={renewalDayInvalid}>
                  <TextField
                    type="number"
                    label="Renewal day"
                    value={formik.values.renewal_day}
                    inputProps={{ min: 1, max: 31, step: 1 }}
                    error={renewalDayInvalid}
                    onChange={(e) =>
                      formik.setFieldValue("renewal_day", e.target.value)
                    }
                  />
                  <FormHelperText error={renewalDayInvalid}>
                    Optional. Day of the month (1-31) the data usage renews. Leave empty to only set the data limit.
                  </FormHelperText>
                </FormControl>
              </Grid>
            </>
          ) : null}

          <Grid item>
            <FormControl
              error={Boolean(formik.errors.upload)}
              sx={{ width: "100%" }}
            >
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                sx={{ mr: 1 }}
              >
                Upload CSV
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {formik.errors.upload ? (
                <FormHelperText id="upload-error-text">
                  {formik.errors.upload}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid item>
            <FormControl
              error={Boolean(formik.errors.lines)}
              sx={{ width: "100%" }}
            >
              <FormHelperText id="lines-helper-text">
                Add in the format "MSISDN"
              </FormHelperText>
              <TextField
                multiline
                minRows={6}
                placeholder={`Add one MSISDN per line`}
                value={rawData}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const data = e.target.value;
                  setRawData(data);
                  formik.setErrors(validateFunction(data));
                }}
                sx={{ minWidth: 550 }}
              />
              {formik.errors.lines ? (
                <FormHelperText id="lines-error-text">
                  {formik.errors.lines}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid
            container
            sx={{ mt: 3 }}
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <LoadingButton
                style={{ marginRight: "5px" }}
                disabled={
                  formik.errors.lines ||
                  !linesData.length ||
                  renewalDayInvalid ||
                  (formik.values.kosher_api && !formik.values.kosher_reseller_email)
                }
                onClick={() =>
                  !loading && !formik.isSubmitting && formik.submitForm()
                }
                size="large"
                loading={loading}
                variant="contained"
              >
                {"Update Number"}
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default TmobilejUpdateNumberForm;
